import { CreateCourse, DeleteCourse, GetChannelTracks, GetCourseForEvent, UpdateCourse } from '@/connection/courses';
import { FetchError } from '@/connection/helpers';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { getUser } from '@/store/slices/user/selectors';
import { ICourse } from '@/types/courses';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import { useCallback, useEffect, useRef, useState } from 'react';

const useProjectCourse = () => {
  const user = useAppSelector(getUser);
  const workingEvent = useAppSelector(getWorkingProject);

  const [course, setCourse] = useState<ICourse | null>(null);
  const [working, setWorking] = useState(false);
  const fetchedKey = useRef<string | null>(null);

  useEffect(() => {
    const currentKey = `${workingEvent?.uuid}-${user?.active_channel}`;
    const shouldFetch =
      workingEvent && user?.active_channel && !course && !working && fetchedKey.current !== currentKey;

    if (shouldFetch) {
      (async () => {
        try {
          fetchedKey.current = currentKey;
          setWorking(true);
          const [course] = await Promise.all([
            GetCourseForEvent(user.active_channel, workingEvent.uuid),
            GetChannelTracks(user.active_channel, undefined, 'courses'),
          ]);
          setCourse(course);
        } catch (e: unknown) {
          if (e instanceof FetchError && e.response && e.response.status === 404) {
            console.info('Course not found for event, creating new course');
          } else {
            toastTrigger('Course failed to load', {
              type: EToastTypes.error,
              autoClose: 5000,
            });
          }
        } finally {
          setWorking(false);
        }
      })();
    }
    // working is intentionally omitted to prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingEvent, user?.active_channel, course, user?.id]);

  const handleSave = useCallback(
    async (name?: string, description?: string, categories?: string[], credits?: number) => {
      if (!user?.active_channel || !course) return;

      setWorking(true);
      try {
        const newCourse = {
          ...course,
          ...(name && { name: { ...course.name, base: name } }),
          ...(description && { description }),
          ...(categories && { categories }),
          ...(credits && { credits }),
        };

        await UpdateCourse(user.active_channel, course.uuid, newCourse);
        setCourse(newCourse);
      } catch (e) {
        console.error(e);
        toastTrigger('Error updating course', {
          type: EToastTypes.error,
          autoClose: 5000,
        });
      } finally {
        setWorking(false);
      }
    },
    [course, user?.active_channel]
  );

  const handleDelete = useCallback(async () => {
    if (!user?.active_channel || !course) return;

    setWorking(true);
    try {
      await DeleteCourse(user.active_channel, course.uuid);
      setCourse(null);
    } catch (e) {
      console.error(e);
      toastTrigger('Error deleting course', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    } finally {
      setWorking(false);
    }
  }, [course, user?.active_channel]);

  const courseCanBeAddedToEvent = useCallback((): boolean => {
    const coursesCanbeAdded = !!workingEvent && workingEvent.sessions.length === 1;
    if (!coursesCanbeAdded) {
      toastTrigger('Course Project', {
        type: EToastTypes.warning,
        autoClose: 5000,
        description: 'Courses are limited to single session projects',
      });
    }

    return coursesCanbeAdded;
  }, [workingEvent]);

  const initializeCourse = useCallback(async () => {
    if (!user || !workingEvent || !courseCanBeAddedToEvent()) return;

    setWorking(true);
    try {
      const newCourse = await CreateCourse(user.active_channel, {
        event_id: workingEvent.event,
        channel: user.active_channel,
        name: {
          base: '',
          changed: '',
        },
        created_by: 0, // Backend will handle created_by, just required by typescript
        credits: 0,
        categories: [],
      });

      setCourse(newCourse);
    } catch {
      toastTrigger('Error initializing new course', {
        type: EToastTypes.error,
        autoClose: 5000,
      });
    } finally {
      setWorking(false);
    }
  }, [user, workingEvent, courseCanBeAddedToEvent]);

  return {
    course,
    setCourse,
    working,
    workingEvent,
    handleSave,
    handleDelete,
    courseCanBeAddedToEvent,
    initializeCourse,
  };
};

export default useProjectCourse;
